/**
 * Factory: Verify KZG proof
 *
 * @param {Object} deps - Crypto dependencies
 * @param {(blob: Uint8Array, commitment: Uint8Array, proof: Uint8Array) => boolean} deps.verifyBlobKzgProof - KZG verification function from c-kzg-4844
 * @returns {(blob: import('./BlobType.js').BrandedBlob, commitment: import('./BlobType.js').Commitment, proof: import('./BlobType.js').Proof) => boolean} Function that verifies KZG proof
 *
 * @see https://voltaire.tevm.sh/primitives/blob for Blob documentation
 * @since 0.0.0
 * @throws {Error} If blob, commitment, or proof size is invalid or c-kzg-4844 library not available
 * @example
 * ```javascript
 * import { Verify } from './primitives/Blob/index.js';
 * import { verifyBlobKzgProof } from 'c-kzg';
 *
 * const verify = Verify({ verifyBlobKzgProof });
 * const isValid = verify(blob, commitment, proof);
 * ```
 */
import { InvalidLengthError, PrimitiveError } from "../errors/index.js";
import { SIZE } from "./constants.js";

export function Verify(
	/** @type {{verifyBlobKzgProof: (blob: Uint8Array, commitment: Uint8Array, proof: Uint8Array) => boolean}} */ {
		verifyBlobKzgProof,
	},
) {
	return /** @param {Uint8Array} blob @param {Uint8Array} commitment @param {Uint8Array} proof */ function verify(
		blob,
		commitment,
		proof,
	) {
		if (blob.length !== SIZE) {
			throw new InvalidLengthError(`Invalid blob size: ${blob.length}`, {
				value: blob.length,
				expected: `${SIZE} bytes`,
				code: -32602,
				docsPath: "/primitives/blob/verify#error-handling",
			});
		}
		if (commitment.length !== 48) {
			throw new InvalidLengthError(
				`Invalid commitment size: ${commitment.length}`,
				{
					value: commitment.length,
					expected: "48 bytes",
					code: -32602,
					docsPath: "/primitives/blob/verify#error-handling",
				},
			);
		}
		if (proof.length !== 48) {
			throw new InvalidLengthError(`Invalid proof size: ${proof.length}`, {
				value: proof.length,
				expected: "48 bytes",
				code: -32602,
				docsPath: "/primitives/blob/verify#error-handling",
			});
		}
		try {
			return verifyBlobKzgProof(blob, commitment, proof);
		} catch (error) {
			throw new PrimitiveError(
				`Failed to verify KZG proof: ${error instanceof Error ? error.message : String(error)}`,
				{
					code: -32000,
					docsPath: "/primitives/blob/verify#error-handling",
					cause: error instanceof Error ? error : undefined,
				},
			);
		}
	};
}
